import {
    ChildTextContentElement,
    OptionItemState,
    TextContentElement,
} from '@deltek/specification-client/core/Models/textContentElement'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { handleOptionGroupUpdate } from './optionGroupHelpers'
import { UnitsOfMeasure } from 'models/projectSettings'

/**
 * Updates a regular (non-OptionGroup) element.
 * For regular elements: only update the target element to OptedIn.
 */
export const handleRegularElementUpdate = (
    elements: ChildTextContentElement[],
    targetId: string
): ChildTextContentElement[] => {
    if (!elements?.length || !targetId) {
        return elements || []
    }

    return elements.map((el) =>
        el.id === targetId
            ? {
                  ...el,
                  optionItemState: 'OptedIn' as OptionItemState,
              }
            : el
    )
}

/**
 * Validates if an element exists within a collection.
 */
const elementExists = (
    elements: ChildTextContentElement[],
    targetId: string
): boolean => {
    return elements?.some((el) => el.id === targetId) ?? false
}

/**
 * Updates a specific element by ID within a content item.
 *
 * @param contentItem - The content item containing elements to update
 * @param updatedContentItem - The content item being updated
 * @param targetId - The ID of the element to update
 * @param value - The new option item state value to set
 * @returns A new content item with the updated element
 */
export const updateElementById = (
    contentItem: TextContentElement,
    updatedContentItem: TextContentElement,
    targetId: string,
    value: OptionItemState
): TextContentElement => {
    // Input validation
    if (!contentItem || !targetId || !value) {
        return contentItem
    }

    const elements = contentItem.elements || []

    if (
        !elements.length ||
        !elementExists(elements as ChildTextContentElement[], targetId)
    ) {
        return contentItem
    }

    const isOptionGroup = contentItem.type === NODE_TYPES.OptionGroup

    try {
        // Create updated elements based on the contentItem type
        const updatedElements = isOptionGroup
            ? handleOptionGroupUpdate(
                  elements as ChildTextContentElement[],
                  updatedContentItem,
                  targetId,
                  value
              )
            : handleRegularElementUpdate(
                  elements as ChildTextContentElement[],
                  targetId
              )

        return {
            ...contentItem,
            elements: updatedElements,
        }
    } catch (error) {
        console.error('Error updating element:', error)
        return contentItem
    }
}

/**
 * Updates a node's elements by creating a new array with the updated elements.
 */
const updateNodeElements = (
    node: ContentItemViewNodesType,
    nodeId: string,
    elements: TextContentElement[]
): ContentItemViewNodesType => {
    if (node.id === nodeId) {
        return { ...node, elements }
    }
    return node
}

/**
 * Updates nested elements within a node (for pill updates).
 */
const updateNestedElements = (
    node: ContentItemViewNodesType,
    nodeId: string,
    elements: TextContentElement[]
): ContentItemViewNodesType => {
    if (!node.elements?.length) {
        return node
    }

    const updatedElements = node.elements.map((element) => {
        if (element.id === nodeId) {
            return { ...element, elements }
        }
        return element
    })

    return { ...node, elements: updatedElements }
}

/**
 * Updates the text content of a node in the content item view nodes.
 * @param nodeId - The ID of the node to update.
 * @param elements - updated elements array.
 * @param nodes - The array of content item view nodes.
 * @param isUpdatingPill - boolean to check if we are updating pills
 * @returns The updated array of content item view nodes.
 */
export const updateElements = (
    nodeId: string,
    elements: TextContentElement[],
    nodes: ContentItemViewNodesType[],
    isUpdatingPill: boolean = false
): ContentItemViewNodesType[] => {
    // Input validation
    if (!nodeId || !elements || !nodes?.length) {
        return nodes || []
    }

    let hasUpdated = false

    const updatedNodes = nodes.map((node) => {
        if (!node.elements) return node

        // Direct node update
        if (node.id === nodeId) {
            hasUpdated = true
            return updateNodeElements(node, nodeId, elements)
        }

        // Nested element update for pills
        if (isUpdatingPill && node.elements.length > 0) {
            const hasNestedTarget = node.elements.some(
                (element) => element.id === nodeId
            )
            if (hasNestedTarget) {
                hasUpdated = true
                return updateNestedElements(node, nodeId, elements)
            }
        }

        return node
    })

    // Log warning if no updates were made
    if (!hasUpdated) {
        console.warn(`No element found with ID: ${nodeId}`)
    }

    return updatedNodes
}

/**
 * Utility function to batch update multiple elements efficiently.
 */
export const updateMultipleElements = (
    nodes: ContentItemViewNodesType[],
    updates: Array<{
        nodeId: string
        elements: TextContentElement[]
        isUpdatingPill?: boolean
    }>
): ContentItemViewNodesType[] => {
    return updates.reduce(
        (currentNodes, { nodeId, elements, isUpdatingPill }) =>
            updateElements(nodeId, elements, currentNodes, isUpdatingPill),
        nodes
    )
}
